import { useRef, useState } from 'react'
import { useLocation } from 'wouter'
import { Mail, Lock, Trash2, Loader2, ChevronLeft, ChevronRight, UserPen, AtSign, FileText, ShieldIcon, LogOutIcon, PencilIcon, CreditCard as CreditCardIcon, ShieldCheck, Wallet } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/hooks/use-user'
import { apiFetch } from '@/utils'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { signOut } from 'firebase/auth'
import { auth } from '@/constants'
import ImageCropper, { ImageCropperRef } from '@/components/ui/image-cropper'
import { getFileNameFromUrl } from '@/utils/imageUrls'
import { FEATURES } from '@/lib/config'

export default function AccountPage() {
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const { email, displayName, disconnectWallet, isConnected, profileImageUrl, setUserData } = useUser()

	const [isUploadingImage, setIsUploadingImage] = useState(false)
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [isLoggingOut, setIsLoggingOut] = useState(false)

	const cropperRef = useRef<ImageCropperRef>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const accountActionItems = [
		{ label: 'Edit Profile', icon: UserPen, onClick: () => navigate('/account/edit-profile') },
		{ label: 'Change Username', icon: AtSign, onClick: () => navigate('/account/change-username') },
		{ label: 'Change Email', icon: Mail, onClick: () => navigate('/account/change-email') },
		{ label: 'Change Password', icon: Lock, onClick: () => navigate('/account/change-password') },
		// Two-Factor Authentication is hidden until Firebase Identity Platform is on the
		// Blaze plan (TOTP enrollment fails with auth/operation-not-allowed otherwise).
		...(FEATURES.MFA_ENABLED
			? [{ label: 'Two-Factor Authentication', icon: ShieldCheck, onClick: () => navigate('/account/two-factor-auth') }]
			: []),
		{ label: 'Linked Wallets', icon: Wallet, onClick: () => navigate('/account/linked-wallets') },
		{ label: 'Payment Methods', icon: CreditCardIcon, onClick: () => navigate('/account/payment-methods') },
		{ label: 'Terms of Use', icon: FileText, onClick: () => navigate('/terms-of-use') },
		{ label: 'Privacy Policy', icon: ShieldIcon, onClick: () => navigate('/privacy-policy') },
		{ label: 'Delete Account', icon: Trash2, onClick: () => navigate('/account/delete-account') },
		{ label: 'Sign Out', icon: LogOutIcon, onClick: () => setShowConfirmModal(true) }
	]

	// Handle profile image upload
	const handleImageUpload = async (file: File) => {
		if (!file) return

		if (!file.type.startsWith('image/')) {
			toast({
				title: 'Invalid File',
				description: 'Please select an image file.',
				variant: 'destructive'
			})
			return
		}

		if (file.size > 5 * 1024 * 1024) {
			toast({
				title: 'File Too Large',
				description: 'Please select an image smaller than 5MB.',
				variant: 'destructive'
			})
			return
		}
		const formData = new FormData();
		formData.append("image", file);

		setIsUploadingImage(true)
		try {
			const res = await apiFetch(
			`/v1/me/upload-avatar`,
			{
				method: "POST",
				body: formData,
				withCredentials: true,
				mulipartHeaderTypeSet: true,
			}
			);

			const data = await res.json();
			if (!res.ok) throw new Error(data.error?.message || "Failed to upload image");

			setUserData(data.data.user);

			toast({
				title: "Image Uploaded",
				description: "Your profile image has been updated."
			});
		} catch (error) {
			toast({
				title: 'Upload Failed',
				description: error instanceof Error ? error.message : 'Failed to upload image',
				variant: 'destructive'
			})
		} finally {
			setIsUploadingImage(false)
		}
	}

	const handleLogOut = async () => {
		try {
			setIsLoggingOut(true)
			document.cookie = `session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
			await signOut(auth)

			setUserData(null)
			if (isConnected) {
				await disconnectWallet()
			}
			navigate('/login')
		}
		finally {
			setIsLoggingOut(false)
		}
	}

	// Get user initials for avatar
	const userInitials = displayName
		? displayName
				.split(' ')
				.map((name: string) => name[0])
				.join('')
				.toUpperCase()
				.slice(0, 2)
		: email?.slice(0, 2).toUpperCase() || 'U'


		return (
			<div className="h-[100dvh] bg-[#18181B] text-white flex flex-col">
			  <div className="max-w-5xl w-full mx-auto flex flex-col flex-1">
				{/* Header with back button */}
				<div
					role="banner"
					className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-start gap-2 text-white py-3 px-5"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>
					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">Account</h1>
				</div>

				{/* Center content */}
				<div className="flex-1 flex items-center justify-center px-5 pb-20">
				<div className="bg-[#212124] rounded-3xl py-8 px-4 flex flex-col items-center w-full max-w-sm">
				  {/* Avatar */}
				  <div className='flex items-center flex-col mb-6'>
				  	<div className="relative mb-4">
						<div className="absolute inset-0 rounded-full bg-[#EF4444] blur-lg opacity-40" />

						{profileImageUrl ? (
							<img src={profileImageUrl} alt="Profile" className="relative h-24 w-24 rounded-full object-cover border-2 border-[#EF4444]" onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }} />
						) : (
							<div className="relative h-24 w-24 rounded-full bg-[#2F2F32] border-2 border-[#EF4444] flex items-center justify-center text-2xl font-semibold" onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }} >
								{userInitials}
							</div>
						)}

						{/* Edit Icon */}
						<div className="absolute bottom-1 right-1 h-[24px] w-[24px] rounded-full bg-[#FFFFFF] flex items-center justify-center cursor-pointer" onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}>
							{isUploadingImage ? (
								<Loader2 className="h-4 w-4 animate-spin text-[#EF4444]" />
							) : (
								<PencilIcon className="h-4 w-4" fill="#EF4444" stroke="#FFFFFF" strokeWidth={1}/>
							)}
						</div>
						<input
							ref={fileInputRef}
							type="file"
							accept="image/*"
							hidden
							onChange={(e) => {
							const file = e.target.files?.[0];
							if (!file) return;
							cropperRef.current?.openWithFile(file);
							e.target.value = "";
							}}
						/>

						<ImageCropper
							ref={cropperRef}
							initialImage={profileImageUrl || undefined}
							initialFileName={getFileNameFromUrl(
								profileImageUrl
							)}
							aspectRatio={1}
							isPreviewVisible={false}
							onSave={async ({ croppedUrl, fileName }) => {
								const blob = await (await fetch(croppedUrl)).blob();
								const file = new File([blob], fileName, { type: blob.type });
								handleImageUpload(file);
							}}
						/>
					</div>

					{/* Name + Email */}
					<h2 className="font-montserrat text-xl leading-[18px] font-bold mb-1">
						Hi, {displayName?.split(' ')[0] || 'User'}!
					</h2>
					<p className="font-montserrat leading-[18px] text-sm text-[#FFFFFF]">{email}</p>
				  </div>


					{/* ACTION LIST */}
					<div className="space-y-2 w-full mt-6">
					{accountActionItems?.map((item) => (
						<button
						key={item.label}
						onClick={item.onClick}
						className="w-full h-[42px] bg-[#2F2F32] rounded-lg px-4 py-4 flex items-center justify-between">
						<div className="flex items-center gap-3">
							<item.icon className="h-[20px] w-[20px] text-white" />
							<span className="font-figtree leading-[16x] text-sm font-regular">{item.label}</span>
						</div>
						<ChevronRight className="h-[20px] w-[20px] text-[#FFFFFF]" />
						</button>
					))}
					</div>
				</div>
				</div>
			  </div>

			<AlertDialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
				<AlertDialogContent className="rounded-2xl shadow-lg border fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 border-[#2F2F32] p-6 bg-[#18181B] w-[90%] sm:w-full max-w-sm mx-auto">
					<AlertDialogHeader className="space-y-2 text-center sm:text-center">
						<AlertDialogTitle className="text-lg font-semibold text-white font-figtree text-center">
							Are you sure you want to Sign Out?
						</AlertDialogTitle>
						<AlertDialogDescription className="text-sm text-[#BABABB] font-figtree text-center">
							You will be logged out from your current session. You can log back in anytime.
						</AlertDialogDescription>
					</AlertDialogHeader>

					<AlertDialogFooter className="flex flex-col gap-3 sm:flex-col sm:justify-center sm:space-x-0 items-center">
						<Button
							onClick={(e) => {
								e.preventDefault()
								handleLogOut()
							}}
							disabled={isLoggingOut}
							className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96] hover:bg-[#dc2626]"
						>
							{isLoggingOut ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Signing out...
								</>
							) : (
								'Sign Out'
							)}
						</Button>
						<AlertDialogCancel
							onClick={() => setShowConfirmModal(false)}
							className="w-full h-[42px] !mt-0 text-sm leading-[14px] font-semibold font-figtree rounded-[26px] bg-white text-[#18181B] hover:bg-gray-200"
						>
							Cancel
						</AlertDialogCancel>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			</div>
		)
}
